/**
 * AnalysisSidePanel - Sliding panel for displaying advanced analysis results
 * Slides in from the right side and displays AnalysisResults in sideBySide mode
 */

import React, {useEffect} from "react";
import {RingLoader} from "react-spinners";
import {AnalysisResult} from "../../types/advancedAnalysis";
import AnalysisResults from "./AnalysisResults";

export interface AnalysisSidePanelProps {
  isOpen: boolean;
  analysisResult: AnalysisResult | null;
  isLoading?: boolean;
  onClose: () => void;
  onReanalyze?: () => void;
  cacheAgeMinutes?: number | null;
  isDemo?: boolean;
}

const AnalysisSidePanel: React.FC<AnalysisSidePanelProps> = ({
  isOpen,
  analysisResult,
  isLoading = false,
  onClose,
  onReanalyze,
  cacheAgeMinutes,
  isDemo = false
}) => {
  // Handle ESC key to close panel
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={`analysis-side-panel ${isOpen ? "open" : ""}`}>
      <div className="analysis-side-panel-content">
        {isLoading ? (
          <div className="text-center py-5">
            <div className="mb-3">
              <RingLoader color="#4682b4" size={80} loading={true} />
            </div>
            <h5 className="text-muted">Loading Analysis...</h5>
            <p className="text-muted">
              <small>Fetching your advanced performance analysis</small>
            </p>
          </div>
        ) : analysisResult ? (
          <AnalysisResults
            result={analysisResult}
            cacheAgeMinutes={cacheAgeMinutes}
            onReanalyze={onReanalyze}
            onClose={onClose}
            layoutMode="sideBySide"
            isDemo={isDemo}
          />
        ) : (
          <div className="text-center py-5">
            <i className="fa fa-exclamation-circle fa-3x text-warning mb-3"></i>
            <h5 className="text-muted">No Analysis Data</h5>
            <p className="text-muted">
              <small>Unable to load analysis results</small>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisSidePanel;
